"use client"

import type { FullTranslation } from "../../lib/i18n"
import { useTr } from "../../lib/use-tr"
import { Reveal } from "../reveal"
import { Sparkles } from "lucide-react"

const BENEFITS_T = {
  eyebrow: { FR: "Bénéfices", EN: "Benefits" },
  headline: {
    FR: "Les bénéfices pour vos équipes",
    EN: "The benefits for your teams",
  },
  subhead: {
    FR: "Moins de travail manuel, des cibles mieux qualifiées, et une prospection qui passe à l'échelle sans effort.",
    EN: "Less manual work, better-qualified targets, and prospecting that scales without the headcount.",
  },

  s1_label: {
    FR: "économisées par semaine et par commercial",
    EN: "saved per week, per rep",
  },
  s1_body: {
    FR: "La recherche, l'enrichissement et la qualification tournent en continu, sans intervention manuelle.",
    EN: "Research, enrichment and qualification run continuously, with zero manual work.",
  },

  s2_label: {
    FR: "de réponses en plus qu'un cold email générique",
    EN: "more replies than generic cold email",
  },
  s2_body: {
    FR: "Chaque message part d'un angle personnalisé, construit sur des données réelles du prospect.",
    EN: "Every message opens with a personalized angle, built from real prospect data.",
  },

  s3_label: {
    FR: "des prospects contactés avec un signal d'affaires",
    EN: "of prospects contacted with a business signal",
  },
  s3_body: {
    FR: "Notre IA ne retient que les entreprises qui montrent un besoin réel, détecté avant le premier contact.",
    EN: "Our AI only keeps businesses showing real need, detected before first contact.",
  },

  s4_label: {
    FR: "recrutement nécessaire pour passer à l'échelle",
    EN: "extra hires needed to scale",
  },
  s4_body: {
    FR: "Multipliez les campagnes multi-canales sans faire grossir l'équipe.",
    EN: "Multiply multi-channel campaigns without growing the team.",
  },
} satisfies FullTranslation

type Stat = {
  value: string
  labelKey: keyof typeof BENEFITS_T
  bodyKey: keyof typeof BENEFITS_T
}

const STATS: Stat[] = [
  { value: "15h", labelKey: "s1_label", bodyKey: "s1_body" },
  { value: "×3", labelKey: "s2_label", bodyKey: "s2_body" },
  { value: "100%", labelKey: "s3_label", bodyKey: "s3_body" },
  { value: "0", labelKey: "s4_label", bodyKey: "s4_body" },
]

export function Benefits() {
  const tr = useTr(BENEFITS_T)

  return (
    <section
      aria-labelledby="benefits-heading"
      className="py-16 sm:py-20 md:py-24"
    >
      <Reveal className="sm:container">
        <div className="mb-14 max-w-2xl text-left">
          <div className="bg-card/60 text-muted-foreground mb-5 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium backdrop-blur-sm">
            <Sparkles className="text-primary-cta size-3.5" aria-hidden />
            <span>{tr("eyebrow")}</span>
          </div>
          <h2
            id="benefits-heading"
            className="text-foreground font-display leading-display tracking-dense text-3xl font-semibold sm:text-4xl md:text-5xl"
          >
            {tr("headline")}
          </h2>
          <p className="text-muted-foreground text-md mt-4 max-w-md leading-relaxed sm:text-base">
            {tr("subhead")}
          </p>
        </div>

        <dl className="grid gap-x-10 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
          {STATS.map(({ value, labelKey, bodyKey }) => (
            <div
              key={labelKey}
              className="border-border/60 flex flex-col border-t pt-6"
            >
              {/* A dl group must start with its dt, so the term comes first in
                  the DOM and the order-* classes restore the visual order. */}
              <dt className="text-primary-cta-text text-2sm order-2 mt-3 leading-snug font-semibold">
                {tr(labelKey)}
              </dt>
              <dd className="text-foreground font-display order-1 text-5xl font-semibold tracking-tighter tabular-nums sm:text-6xl">
                {value}
              </dd>
              <dd className="text-muted-foreground text-2sm order-3 mt-2 leading-relaxed">
                {tr(bodyKey)}
              </dd>
            </div>
          ))}
        </dl>
      </Reveal>
    </section>
  )
}
